(() => {
  'use strict';

  const detectDelimiter = (text) => {
    const firstRecord = text.split(/\r?\n/, 1)[0] || '';
    return [',', ';', '\t'].reduce((best, candidate) => {
      let count = 0;
      let quoted = false;
      for (let index = 0; index < firstRecord.length; index += 1) {
        const character = firstRecord[index];
        if (character === '"') quoted = !quoted;
        else if (!quoted && character === candidate) count += 1;
      }
      return count > best.count ? { value: candidate, count } : best;
    }, { value: ',', count: -1 }).value;
  };

  const parseCsv = (source) => {
    const text = String(source || '').replace(/^\uFEFF/, '');
    const delimiter = detectDelimiter(text);
    const records = [];
    let record = [];
    let field = '';
    let quoted = false;

    for (let index = 0; index < text.length; index += 1) {
      const character = text[index];
      if (quoted) {
        if (character === '"' && text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else if (character === '"') quoted = false;
        else field += character;
        continue;
      }

      if (character === '"') quoted = true;
      else if (character === delimiter) {
        record.push(field);
        field = '';
      } else if (character === '\n' || character === '\r') {
        if (character === '\r' && text[index + 1] === '\n') index += 1;
        record.push(field);
        if (record.some((value) => value.trim() !== '')) records.push(record);
        record = [];
        field = '';
      } else field += character;
    }

    record.push(field);
    if (record.some((value) => value.trim() !== '')) records.push(record);
    if (records.length < 2) return [];

    const headers = records.shift().map((header) => header.trim());
    return records.map((values) => Object.fromEntries(headers.map((header, index) => [header, String(values[index] || '').trim()])));
  };

  const fieldValue = (review, names) => {
    for (const name of names) {
      const value = review[name];
      if (value != null && String(value).trim() !== '') return String(value).trim();
    }
    return '';
  };

  const toPlainText = (value) => {
    let text = String(value || '').trim();
    if (!text) return '';

    for (let pass = 0; pass < 3; pass += 1) {
      text = text
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(?:p|div|li|h[1-6]|tr|section|article)>/gi, '\n')
        .replace(/<li\b[^>]*>/gi, '\n• ')
        .replace(/>\s*</g, '> <');

      const parsed = new DOMParser().parseFromString(text, 'text/html');
      parsed.querySelectorAll('script, style, template, noscript').forEach((element) => element.remove());
      const decoded = parsed.body.textContent || '';
      if (decoded === text || !/[<&]/.test(decoded)) {
        text = decoded;
        break;
      }
      text = decoded;
    }

    return text
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/\s*(Pluspunten|Minpunten|Verbeterpunten|Aandachtspunten)\s*:?[ \t]*/gi, '\n$1: ')
      .replace(/\s*\n\s*/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/ {2,}/g, ' ')
      .trim();
  };

  const privacySafeAuthor = (value) => {
    let name = toPlainText(value).replace(/\s+/g, ' ').trim();
    if (!name || name.includes('@')) return 'Anoniem';
    if (/^(?:familie|fam\.?)\s+/i.test(name)) return 'Anoniem';

    const titlePrefix = /^(?:dhr\.?|de heer|mevr\.?|mevrouw|meneer)\s+/i;
    const hadTitle = titlePrefix.test(name);
    name = name.replace(titlePrefix, '').trim();
    const commaParts = name.split(',').map((part) => part.trim()).filter(Boolean);
    if (commaParts.length > 1) name = commaParts[1];

    const first = (name.split(/\s+/)[0] || '').replace(/^[^\p{L}]+|[^\p{L}.'’\-]+$/gu, '');
    if (!first) return 'Anoniem';
    if (hadTitle || /^\p{L}\.?$/u.test(first)) return `${first.charAt(0).toLocaleUpperCase('nl-NL')}.`;
    return first;
  };

  const parseScore = (value) => {
    const original = String(value || '').trim();
    const numeric = Number.parseFloat(original.replace(',', '.').replace(/[^\d.]/g, ''));
    if (!Number.isFinite(numeric)) return null;
    const maximum = numeric > 5 ? 10 : 5;
    const fivePointScore = maximum === 10 ? numeric / 2 : numeric;
    return {
      label: original.includes('/') ? original : `${original || numeric}/${maximum}`,
      numeric,
      maximum,
      fivePointScore,
      stars: Math.max(0, Math.min(5, Math.round(fivePointScore)))
    };
  };

  const normalizeReview = (review, defaults) => ({
    title: toPlainText(fieldValue(review, ['Beschrijf uw ervaring in een regel', 'Titel', 'Reviewtitel'])),
    text: toPlainText(fieldValue(review, ['Beschrijf uw ervaring', 'Review', 'Ervaring'])),
    author: privacySafeAuthor(fieldValue(review, ['Naam', 'Klant', 'Auteur'])),
    score: parseScore(fieldValue(review, ['Totaal score', 'Score', 'Rating'])),
    sourceName: toPlainText(fieldValue(review, ['Bron', 'Source', 'Platform'])) || defaults.sourceName,
    sourceUrl: fieldValue(review, ['Bron URL', 'Source URL', 'Platform URL']) || defaults.sourceUrl
  });

  const shuffle = (items) => {
    const pool = [...items];
    for (let index = pool.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [pool[index], pool[randomIndex]] = [pool[randomIndex], pool[index]];
    }
    return pool;
  };

  const mixPageReviews = (positive, moderate, moderateCount) => {
    const result = [...positive];
    shuffle(moderate).slice(0, moderateCount).forEach((review, index) => {
      const position = Math.min(result.length, (index + 1) * 12);
      result.splice(position, 0, review);
    });
    return result;
  };

  const createReview = (review) => {
    const article = document.createElement('article');
    article.className = 'pw-home-review';

    if (review.score) {
      const rating = document.createElement('div');
      rating.className = 'pw-home-review__rating';
      rating.setAttribute('aria-label', `Beoordeling ${review.score.label}`);

      const stars = document.createElement('span');
      stars.className = 'pw-home-review__stars';
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = `${'★'.repeat(review.score.stars)}${'☆'.repeat(5 - review.score.stars)}`;

      const score = document.createElement('span');
      score.className = 'pw-home-review__score';
      score.textContent = review.score.label;
      rating.append(stars, score);
      article.append(rating);
    }

    if (review.title) {
      const heading = document.createElement('h3');
      heading.textContent = review.title;
      article.append(heading);
    }

    if (review.text) {
      const paragraph = document.createElement('p');
      paragraph.className = 'pw-home-review__text';
      paragraph.textContent = review.text;
      article.append(paragraph);
    }

    const meta = document.createElement('p');
    meta.className = 'pw-home-review__meta';

    const author = document.createElement('span');
    author.className = 'pw-home-review__author';
    author.textContent = review.author || 'Anoniem';
    meta.append(author);

    if (review.sourceName) {
      const source = review.sourceUrl ? document.createElement('a') : document.createElement('span');
      source.className = 'pw-home-review__source';
      source.textContent = `Bron: ${review.sourceName}`;
      if (review.sourceUrl) {
        source.href = review.sourceUrl;
        source.target = '_blank';
        source.rel = 'noopener noreferrer';
      }
      meta.append(source);
    }

    article.append(meta);
    return article;
  };

  const setupCarousel = (section, list) => {
    const controls = section.querySelector('[data-pw-review-controls]');
    const previous = section.querySelector('[data-pw-review-prev]');
    const next = section.querySelector('[data-pw-review-next]');
    if (!controls || !previous || !next) return;

    const update = () => {
      const maximum = list.scrollWidth - list.clientWidth;
      controls.hidden = maximum < 4;
      previous.disabled = list.scrollLeft <= 4;
      next.disabled = list.scrollLeft >= maximum - 4;
    };

    const distance = () => {
      const card = list.querySelector('.pw-home-review');
      return card ? card.getBoundingClientRect().width + 14 : list.clientWidth;
    };

    previous.addEventListener('click', () => list.scrollBy({ left: -distance(), behavior: 'smooth' }));
    next.addEventListener('click', () => list.scrollBy({ left: distance(), behavior: 'smooth' }));
    list.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    requestAnimationFrame(update);
  };

  const initialize = async (section) => {
    if (!section || section.dataset.pwReviewsReady === 'true') return;
    section.dataset.pwReviewsReady = 'true';

    const list = section.querySelector('[data-pw-reviews-list]');
    const loading = section.querySelector('[data-pw-reviews-loading]');
    const status = section.querySelector('[data-pw-reviews-status]');
    const url = section.dataset.reviewsUrl;
    const view = section.dataset.reviewView || 'home';
    const sourceName = section.dataset.sourceName || 'Kiyoh';
    const sourceUrl = section.dataset.sourceUrl || '';
    if (!list || !url) return;

    try {
      const response = await fetch(url, { credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const normalized = parseCsv(await response.text())
        .map((review) => normalizeReview(review, { sourceName, sourceUrl }))
        .filter((review) => (review.title || review.text) && review.score);

      const positiveThreshold = Number.parseFloat(section.dataset.positiveScore || '8') / 2;
      const moderateThreshold = Number.parseFloat(section.dataset.moderateScore || '6') / 2;
      const positive = normalized.filter((review) => review.score.fivePointScore >= positiveThreshold);
      const moderate = normalized.filter((review) => review.score.fivePointScore >= moderateThreshold && review.score.fivePointScore < positiveThreshold);

      loading?.remove();
      if (!positive.length) throw new Error('Geen beoordelingen gevonden');

      if (view === 'home') {
        const count = Number.parseInt(section.dataset.reviewCount || '12', 10);
        shuffle(positive).slice(0, count).forEach((review) => list.append(createReview(review)));
        setupCarousel(section, list);
        return;
      }

      const moderateCount = Number.parseInt(section.dataset.moderateCount || '3', 10);
      const pageSize = Number.parseInt(section.dataset.pageSize || '18', 10);
      const reviews = mixPageReviews(positive, moderate, moderateCount);
      const moreWrap = section.querySelector('[data-pw-review-more-wrap]');
      const moreButton = section.querySelector('[data-pw-review-more]');
      let shown = 0;

      const renderMore = () => {
        reviews.slice(shown, shown + pageSize).forEach((review) => list.append(createReview(review)));
        shown = Math.min(reviews.length, shown + pageSize);
        if (moreWrap) moreWrap.hidden = shown >= reviews.length;
      };

      moreButton?.addEventListener('click', renderMore);
      renderMore();
    } catch (error) {
      loading?.remove();
      if (status) {
        status.textContent = 'De klantbeoordelingen konden op dit moment niet worden geladen.';
        status.hidden = false;
      }
      console.debug('Klantreviews konden niet worden geladen.', error);
    }
  };

  const initializeAll = (scope = document) => scope.querySelectorAll?.('[data-pw-reviews]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
